import UniversalApiClient, { strapiClient } from './UniversalApiClient';
import { encryptValue, isEncryptionAvailable } from '../utils/crypto';

// Base configuration unified via UniversalApiClient (no hardcoded hosts)
let loop = 0;
const apiClient = strapiClient;

// API Service Class
class ApiService {
  static async authRequest(method, path, data = undefined) {
    const usersPermissionsUrl = `/users-permissions/auth/${path}`;

    const execute = (url) => {
      if (method.toLowerCase() === 'get') return apiClient.get(url);
      if (method.toLowerCase() === 'put') return apiClient.put(url, data || {});
      return apiClient.post(url, data || {});
    };

    return execute(usersPermissionsUrl);
  }

  static broadcastPointsUpdate(newPoints) {
    const next = Number(newPoints);
    if (!Number.isFinite(next)) return;

    // Keep cached user in sync (many parts of the app bootstrap from localStorage)
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        localStorage.setItem('user', JSON.stringify({ ...parsed, points: next }));
      }
    } catch (e) {
      // ignore
    }

    // Emit an event for real-time UI consumers (e.g., navbar)
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('gg:points', { detail: { points: next } }));
      }
    } catch (e) {
      // ignore
    }
  }

  // Unified point mutation normalization
  // Raw shapes consumed:
  //  - Scan: { pointsEarned, newTotalPoints }
  //  - Spin: { pointsWon, newTotalPoints }
  //  - Redemption: { remainingPoints, pointsDeducted }
  // Returns standard shape:
  //  {
  //    success: boolean,
  //    message: string,
  //    pointsChange: number,      // positive for earn, negative for spend
  //    newTotalPoints: number,    // resulting balance
  //    previousPoints: number|undefined,
  //    source: 'scan'|'spin'|'redemption'|'unknown',
  //    data: any                  // raw original payload
  //  }
  static normalizePointMutation(raw, source) {
    if (!raw) return { success: false, message: 'No data', pointsChange: 0, newTotalPoints: 0, previousPoints: undefined, source: source || 'unknown', data: raw };
    let pointsChange = 0;
    let newTotalPoints = 0;
    let previousPoints;

    switch (source) {
      case 'scan':
        pointsChange = raw.pointsEarned || 0;
        newTotalPoints = raw.newTotalPoints != null ? raw.newTotalPoints : (raw.remainingPoints || 0);
        break;
      case 'spin':
        pointsChange = raw.pointsWon || 0;
        newTotalPoints = raw.newTotalPoints != null ? raw.newTotalPoints : (raw.remainingPoints || 0);
        break;
      case 'redemption':
        // Spending points: negative change
        if (raw.pointsDeducted != null) {
          pointsChange = -Math.abs(raw.pointsDeducted);
        } else if (raw.cost != null) {
          pointsChange = -Math.abs(raw.cost);
        } else {
          // Fallback: derive from previous/remaining if provided
          if (raw.previousPoints != null && raw.remainingPoints != null) {
            pointsChange = raw.remainingPoints - raw.previousPoints;
          }
        }
        newTotalPoints = raw.remainingPoints != null ? raw.remainingPoints : (raw.newTotalPoints || 0);
        previousPoints = raw.previousPoints;
        break;
      default:
        // Attempt generic mapping
        pointsChange = raw.pointsEarned || raw.pointsWon || 0;
        newTotalPoints = raw.newTotalPoints || raw.remainingPoints || 0;
    }

    return {
      success: raw.success !== false,
      message: raw.message || 'Points updated',
      pointsChange,
      newTotalPoints,
      previousPoints,
      source: source || 'unknown',
      data: raw
    };
  }
  // Authentication APIs
  static async login(credentials) {
    try {
      // SECURE: Encrypt password with server's RSA public key before sending
      let payload = { ...credentials };
      if (isEncryptionAvailable() && payload.password) {
        try {
          payload.password = await encryptValue(payload.password);
          payload.encrypted = true;
        } catch (encErr) {
          console.warn('🔐 RSA encryption unavailable, falling back to TLS-only:', encErr);
        }
      }
      const response = await this.authRequest('post', 'cookie-login', payload);
      UniversalApiClient.setAuthToken(response?.data?.jwt || null);
      return response.data;
    } catch (error) {
      const errorInfo = this.handleApiError(error);
      throw new Error(errorInfo.message);
    }
  }

  static async register(userData) {
    // SECURE: Encrypt password with server's RSA public key before sending
    let payload = { ...userData };
    if (isEncryptionAvailable() && payload.password) {
      try {
        payload.password = await encryptValue(payload.password);
        payload.encrypted = true;
      } catch (encErr) {
        console.warn('🔐 RSA encryption unavailable, falling back to TLS-only:', encErr);
      }
    }
    const response = await this.authRequest('post', 'cookie-register', payload);
    UniversalApiClient.setAuthToken(response?.data?.jwt || null);
    return response.data;
  }

  static async checkUsernameAvailability(username) {
    try {
      const normalizedUsername = String(username || '').trim().toLowerCase();
      const response = await this.authRequest('post', 'check-username', { username: normalizedUsername });
      const available = response?.data?.available ?? response?.data?.data?.available ?? response?.available;
      return Boolean(available);
    } catch (error) {
      console.error('Error checking username availability:', error);
      throw error;
    }
  }

  static async checkEmailAvailability(email) {
    try {
      const normalizedEmail = String(email || '').trim().toLowerCase();
      const response = await this.authRequest('post', 'check-email', { email: normalizedEmail });
      const available = response?.data?.available ?? response?.data?.data?.available ?? response?.available;
      return Boolean(available);
    } catch (error) {
      console.error('Error checking email availability:', error);
      throw error;
    }
  }

  static async forgotPassword(data) {
    try {
      const response = await apiClient.post('/auth/forgot-password', data);
      return response.data;
    } catch (error) {
      console.error('ApiService.forgotPassword error:', error);
      const errorInfo = this.handleApiError(error);
      throw new Error(errorInfo.message);
    }
  }

  static async resetPassword(data) {
    try {
      const response = await apiClient.post('/auth/reset-password', data);
      return response.data;
    } catch (error) {
      const errorInfo = this.handleApiError(error);
      throw new Error(errorInfo.message);
    }
  }

  // Logout - clears httpOnly cookie on server
  static async logout() {
    try {
      const response = await this.authRequest('post', 'logout');
      UniversalApiClient.clearAuth();
      return response.data;
    } catch (error) {
      // Don't throw - logout should always succeed locally
      console.warn('Server logout failed, continuing with local logout');
      UniversalApiClient.clearAuth();
      return { success: true };
    }
  }

  // User APIs
  static async getUser() {
    // Cookie-based auth - browser sends httpOnly cookie automatically
    const response = await apiClient.get('/user-actions/me');
    return response.data?.user || response.data;
  }

  // Favorite Brands APIs
  static async getFavoriteBrands(userId) {
    if (!userId) {
      console.warn('getFavoriteBrands called with undefined userId');
      return [];
    }
    try {
      // SECURE: Use middleman endpoint that validates ownership
      const response = await apiClient.get(`/user-actions/favorite-brands`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching favorite brands:', error);
      return [];
    }
  }

  static async addFavoriteBrand(userId, brandName, brandImage) {
    try {
      // SECURE: Use server-side endpoint that validates and handles DB operations
      const response = await apiClient.post('/user-actions/favorite-brands/add', {
        brandName: brandName,
        brandImage: brandImage
      });
      return response.data;
    } catch (error) {
      console.error('Error adding favorite brand:', error);
      throw error;
    }
  }

  static async removeFavoriteBrand(favoriteId) {
    try {
      // SECURE: Use server-side endpoint that validates ownership and handles DB operations
      const response = await apiClient.post('/user-actions/favorite-brands/remove', {
        favoriteId: favoriteId
      });
      return response.data;
    } catch (error) {
      console.error('Error removing favorite brand:', error);
      throw error;
    }
  }

  // Wishlist APIs
  static async getWishlist(userId) {
    try {
      // SECURE: Use middleman endpoint that validates ownership
      const response = await apiClient.get(`/user-actions/wishlist`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      return [];
    }
  }

  static async addToWishlist(userId, product) {
    try {
      // SECURE: Use server-side endpoint that validates and handles DB operations
      const response = await apiClient.post('/user-actions/wishlist/add', {
        productRef: product.id,
        productName: product.displayName || product.productName || product.title,
        productImage: product.image,
        productPrice: product.sellingPrice,
        brandName: product.brandName || ''
      });
      return response.data;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw error;
    }
  }

  static async removeFromWishlist(wishlistId) {
    try {
      // SECURE: Use server-side endpoint that validates ownership and handles DB operations
      const response = await apiClient.post('/user-actions/wishlist/remove', {
        wishlistId: wishlistId
      });
      return response.data;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      throw error;
    }
  }

  static async updateUser(userId, userData) {
    // SECURE: Use server-side endpoint that validates and blocks sensitive fields
    // Server only allows: username, firstName, lastName, phone, address, city, country, zipCode, birthdate
    // Server blocks: points, role, confirmed, blocked, provider, password
    const response = await apiClient.post('/user-actions/profile/update', userData);
    return response.data;
  }

  static async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await apiClient.post('/user-actions/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  static async getCosmetics() {
    const response = await apiClient.get('/user-actions/cosmetics');
    return response.data;
  }

  static async updateCosmetics({ frameColor, badgeImageUrl, badgeName } = {}) {
    const response = await apiClient.post('/user-actions/cosmetics', { frameColor, badgeImageUrl, badgeName });
    return response.data;
  }

  static async changePassword(currentPassword, newPassword, confirmPassword) {
    try {
      let payload = {
        currentPassword,
        password: newPassword,
        passwordConfirmation: confirmPassword
      };
      // SECURE: Encrypt passwords with server's RSA public key
      if (isEncryptionAvailable()) {
        try {
          payload.currentPassword = await encryptValue(currentPassword);
          payload.password = await encryptValue(newPassword);
          payload.passwordConfirmation = await encryptValue(confirmPassword);
          payload.encrypted = true;
        } catch (encErr) {
          console.warn('🔐 RSA encryption unavailable for password change, falling back to TLS-only:', encErr);
        }
      }
      const response = await apiClient.post('/user-actions/change-password', payload);
      return response.data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw this.handleApiError(error);
    }
  }

  static async trackMetadata(metadataType, metadataKey, metadataValue) {
    // SECURE: Use server-side endpoint to track user clicks and preferences
    const response = await apiClient.post('/user-actions/metadata/track', {
      metadataType,
      metadataKey,
      metadataValue
    });
    return response.data;
  }

  // Scan APIs - SECURE: All logic handled server-side
  static async scanBarcode(barcode, userId) {
    if (loop > 0) return;
    loop++;
    console.log('ApiService.scanBarcode called (secure endpoint)');
    
    try {
      // Single secure API call - ALL logic is on the server
      // No points calculation, no duplicate checking, no user updates on client
      const response = await apiClient.post('/scan-barcode/process', 
        { barcode: barcode.trim() }
      );

      // Server returns the complete result
      const result = response.data;
      
      // Normalize and broadcast the update
      const normalized = this.normalizePointMutation({
        success: result.success,
        message: result.message,
        pointsEarned: result.pointsEarned,
        newTotalPoints: result.newTotalPoints,
        previousPoints: result.previousPoints,
        rewardType: result.rewardType
      }, 'scan');
      
      this.broadcastPointsUpdate(normalized.newTotalPoints);
      return normalized;
      
    } catch (error) {
      console.error('Barcode scanning error:', error);
      
      // Handle specific error cases from server
      if (error.response?.status === 409) {
        throw new Error('This barcode was recently scanned. Please wait before scanning again.');
      } else if (error.response?.status === 400) {
        throw new Error(error.response?.data?.error?.message || 'Invalid barcode');
      } else if (error.response?.status === 401) {
        throw new Error('You must be logged in to scan barcodes');
      } else if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      
      throw new Error('Failed to process barcode scan');
    } finally {
      loop = 0;
    }
  }

  // REMOVED: processBarcodeReward - Now handled server-side
  // REMOVED: calculateBarcodeReward - Now handled server-side
  // Security: These functions were moved to backend to prevent client manipulation

  // Gift Card APIs
  static async getGiftCards() {
    try {
      // SECURE: Use middleman endpoint - no direct database access
      const response = await apiClient.get('/user-actions/catalog?limit=100');
      
      // Backend returns: { success: true, data: { products: [], categories: [], pagination: {} } }
      if (response.data && response.data.success) {
        return response.data.data.products || [];
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching gift cards:', error);
      return [];
    }
  }

  static async getGiftCard(giftCardId) {
    const response = await apiClient.get(`/gift-cards/${giftCardId}`);
    return response.data;
  }

  // Redemption APIs
  static async createRedemption(redemptionData) {
    try {
      // SECURE: Use middleman endpoint - all validation server-side
      const productRef = redemptionData?.productRef || redemptionData?.id;

      if (!productRef) {
        throw new Error('productRef is required');
      }

      const response = await apiClient.post('/user-actions/redeem', {
        productRef,
        quantity: redemptionData.quantity || 1
      });

      // Backend returns: { success: true, data: { remainingPoints, redemptionCodes, ... } }
      if (response.data && response.data.success) {
        const normalized = this.normalizePointMutation({
          success: true,
          message: response.data.message || 'Redemption successful!',
          remainingPoints: response.data.data.remainingPoints,
          pointsDeducted: response.data.data.pointsDeducted,
          redemptionCodes: response.data.data.redemptionCodes,
          orderName: response.data.data.orderName,
          previousPoints: response.data.data.previousPoints
        }, 'redemption');
        this.broadcastPointsUpdate(normalized.newTotalPoints);
        return normalized;
      } else {
        throw new Error(response.data?.message || 'Redemption failed');
      }
    } catch (error) {
      console.error('Redemption error:', error);
      const errorMessage = error.response?.data?.error?.message 
        || error.response?.data?.message 
        || error.message 
        || 'Failed to process redemption';
      
      throw new Error(errorMessage);
    }
  }

  static async getUserRedemptions(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }
    // SECURE: Use middleman endpoint that validates ownership
    const response = await apiClient.get(`/user-actions/redemptions`);
    return response.data;
  }

  static async updateRedemptionStatus(redemptionId, status, redemptionCode) {
    // NOTE: This endpoint is deprecated - redemption status should be managed server-side
    // Kept for backwards compatibility but consider removing
    console.warn('Direct redemption status updates are deprecated');
    const response = await apiClient.put(`/redemptions-dbs/${redemptionId}`, {
      data: {
        status,
        redemptionCode,
        completedAt: status === 'completed' ? new Date().toISOString() : null
      }
    });
    return response.data;
  }

  // Point Transaction APIs
  static async getPointTransactions(userId, limit = 50) {
    if (!userId) {
      throw new Error('User ID is required');
    }
    try {
      // SECURE: Use middleman endpoint that validates ownership
      const response = await apiClient.get(`/user-actions/transactions?limit=${limit}`);
      return response.data;
    } catch (error) {
      // If 404 or collection doesn't exist, return empty array
      if (error.response?.status === 404) {
        console.log('Point transactions collection not found or empty');
        return { data: [] };
      }
      throw error;
    }
  }

  static async createPointTransaction(transactionData) {
    // NOTE: This endpoint is deprecated - transactions should only be created server-side
    // via scan, spin, redeem operations
    console.warn('Direct transaction creation is deprecated and blocked');
    throw new Error('Transactions can only be created through secure server operations (scan/redeem/spin)');
  }

  // Reward Code APIs (Admin)
  static async getRewardCodes() {
    const response = await apiClient.get('/reward-codes?populate=*');
    return response.data;
  }

  static async getRewardCodeStats() {
    const response = await apiClient.get('/reward-codes/stats');
    return response.data;
  }

  static async generateRewardCode(codeData) {
    const response = await apiClient.post('/reward-codes/generate', codeData);
    return response.data;
  }

  static async updateRewardCode(codeId, updateData) {
    const response = await apiClient.put(`/reward-codes/${codeId}`, updateData);
    return response.data;
  }

  static async deleteRewardCode(codeId) {
    const response = await apiClient.delete(`/reward-codes/${codeId}`);
    return response.data;
  }

  // Combined data fetching methods for efficiency
  static async getGiftCardsAndUser(userId) {
    const [cardsResponse, userResponse] = await Promise.all([
      this.getGiftCards(),
      this.getUser()
    ]);
    
    return {
      giftCards: cardsResponse.data || cardsResponse,
      user: userResponse
    };
  }

  static async getPointsData(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    try {
      const [transactionsResponse, userResponse] = await Promise.all([
        this.getPointTransactions(userId),
        this.getUser()
      ]);
      
      const transactions = transactionsResponse.data || transactionsResponse || [];
      
      return {
        userData: userResponse,
        history: transactions.map(transaction => {
          const attributes = transaction.attributes || transaction;
          return {
            action: attributes.description || 'Points transaction',
            type: attributes.transactionType === 'earn' ? 'earned' : 'spent',
            points: Math.abs(attributes.points || 0),
            date: attributes.createdAt || new Date().toISOString(),
            metadata: attributes.metadata || null,
            referenceId: attributes.referenceId || null
          };
        })
      };
    } catch (error) {
      console.error('Error fetching points data:', error);
      throw error;
    }
  }

  // Admin Tools APIs
  static async seedTestData() {
    const response = await apiClient.post('/admin-tools/seed-test-data', {});
    return response.data;
  }

  static async getTestUsers() {
    const response = await apiClient.get('/admin-tools/test-users');
    return response.data;
  }

  static async getSystemStats() {
    const response = await apiClient.get('/admin-tools/system-stats');
    return response.data;
  }

  static async resetTestData() {
    const response = await apiClient.post('/admin-tools/reset-test-data', {});
    return response.data;
  }

  // Error handling helper
  static handleApiError(error) {
    let message = 'An unexpected error occurred';
    
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 500:
          message = data?.error?.message || 'Internal server error. Please try again later.';
          break;
        case 404:
          message = 'Resource not found';
          break;
        case 401:
          message = 'Unauthorized. Please log in again.';
          break;
        case 403:
          message = 'Access forbidden. You do not have permission to perform this action.';
          break;
        default:
          message = data?.error?.message || data?.message || `Server error (${status})`;
      }
    } else if (error.code === 'ECONNABORTED') {
      message = 'Request timed out. Please check your connection.';
    } else if (error.message) {
      message = error.message;
    }
    
    console.error('API Error:', {
      status: error.response?.status,
      message,
      data: error.response?.data,
      originalError: error
    });
    
    return {
      success: false,
      message,
      status: error.response?.status,
      data: error.response?.data
    };
  }

  // Chat-related APIs
  static async getChatMessages(roomId = 'public', page = 1, limit = 50, since = null) {
    try {
      const params = { roomId, page, limit };
      if (since) {
        params.since = since;
      }
      const response = await apiClient.get('/user-actions/chat-messages', {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      throw this.handleApiError(error);
    }
  }

  static async sendChatMessage(messageData) {
    try {
      const response = await apiClient.post('/chat-messages', {
        data: messageData
      });
      return response.data;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw this.handleApiError(error);
    }
  }

  static async getChatRooms() {
    try {
      const response = await apiClient.get('/chat-rooms', {
        params: {
          'populate': '*'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching chat rooms:', error);
      throw this.handleApiError(error);
    }
  }

  static async getOnlineUsers(roomId = 'public') {
    try {
      const response = await apiClient.get('/chat/online-users', {
        params: {
          roomId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching online users:', error);
      throw this.handleApiError(error);
    }
  }

  static async updateUserStatus(status = 'online') {
    try {
      // Use user-actions middleman endpoint with cookie auth
      const response = await apiClient.put('/user-actions/update-status', {
        status,
        lastSeen: new Date().toISOString()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw this.handleApiError(error);
    }
  }

  // News-related APIs
  static async getNewsArticles(page = 1, limit = 20, category = null) {
    try {
      const params = {
        'sort': 'publishedAt:desc,createdAt:desc',
        'pagination[page]': page,
        'pagination[pageSize]': limit,
        'populate': '*'
      };

      if (category && category !== 'all') {
        params['filters[category][$eq]'] = category;
      }

      const response = await apiClient.get('/news-articles', {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching news articles:', error);
      throw this.handleApiError(error);
    }
  }

  static async getTrendingNews(limit = 10) {
    // Trending news endpoint doesn't exist - return empty for graceful fallback
    return { data: [] };
  }

  static async getNewsArticle(articleId) {
    try {
      const response = await apiClient.get(`/news-articles/${articleId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching news article:', error);
      throw this.handleApiError(error);
    }
  }

  static async likeNewsArticle(articleId, isLiked) {
    try {
      // First get the current article to get current like count
      const currentArticle = await this.getNewsArticle(articleId);
      const currentLikes = currentArticle.data?.attributes?.likes || currentArticle.data?.likes || 0;

      // Calculate new like count
      const newLikes = isLiked ? currentLikes + 1 : Math.max(0, currentLikes - 1);
      
      // Update user's liked articles using the new secure endpoint
      const currentUser = await this.getUser();
      const currentLikedArticles = currentUser.liked_news_article || [];
      
      let updatedLikedArticles;
      if (isLiked) {
        // Add article if not already liked
        updatedLikedArticles = currentLikedArticles.includes(articleId) 
          ? currentLikedArticles 
          : [...currentLikedArticles, articleId];
      } else {
        // Remove article from user's liked articles
        updatedLikedArticles = currentLikedArticles.filter(id => id !== articleId);
      }
      
      // Use the new secure endpoint
      await this.authRequest('put', 'update-liked-articles', {
        liked_news_articles: updatedLikedArticles
      });
      
      // Update using standard Strapi update endpoint
      // TODO: Create secure middleman endpoint for article likes
      const response = await apiClient.put(`/news-articles/${articleId}`, {
        data: { 
          likes: newLikes 
        }
      });
      
      // Return consistent format
      return {
        data: {
          id: response.data.data?.id || articleId,
          likes: newLikes,
          liked: isLiked
        }
      };
    } catch (error) {
      console.error('Error updating article like:', error);
      console.error('Response:', error.response?.data);
      throw this.handleApiError(error);
    }
  }

  static async addNewsComment(articleId, commentData) {
    try {
      const response = await apiClient.post('/news-comments', {
        data: {
          article: articleId,
          content: commentData.content,
          user: commentData.userId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw this.handleApiError(error);
    }
  }

  static async getUpcomingEvents(limit = 10) {
    try {
      const response = await apiClient.get('/events', {
        params: {
          'filters[eventDate][$gte]': new Date().toISOString(),
          'sort': 'eventDate:asc',
          'pagination[pageSize]': limit
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      return { data: [] };
    }
  }

  static async incrementArticleViews(articleId) {
    try {
    
      // Try custom endpoint first
      const response = await apiClient.put(`/news-articles/${articleId}/view`, {});
      return response.data;
    } catch (error) {
      console.error('Custom view endpoint failed, using standard update:', error);
      try {
        // Fallback: Get current article and increment view count manually
        const currentArticle = await this.getNewsArticle(articleId);
        const currentViews = currentArticle.data?.attributes?.viewCount || currentArticle.data?.viewCount || 0;
        
        await apiClient.put(`/news-articles/${articleId}`, {
          data: { 
            viewCount: currentViews + 1 
          }
        });
        
        return {
          data: {
            id: articleId,
            viewCount: currentViews + 1
          }
        };
      } catch (fallbackError) {
        console.error('Error incrementing article views:', fallbackError);
        return { data: { viewCount: 0 } }; // Return fallback instead of throwing
      }
    }
  }

  static async getUserLikedArticles() {
    try {
      const userData = await this.getUser();
      return userData?.liked_news_article || { data: [] };
    } catch (error) {
      console.error('Error fetching user liked articles:', error);
      return { data: [] };
    }
  }

  static async subscribeToNewsletter(email) {
    try {
      const response = await apiClient.post('/newsletter/subscribe', {
        data: { email }
      });
      return response.data;
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      throw this.handleApiError(error);
    }
  }

  // Inventory APIs
  static async getUserInventory() {
    try {
      // SECURE: Use middleman endpoint - ownership validated
      const response = await apiClient.get('/user-actions/inventory');
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory:', error);
      throw error;
    }
  }

  // Gift Card Order Details API
  static async getGiftCardOrderDetails(orderName) {
    try {
      // SECURE: Use middleman endpoint - ownership validated before revealing codes
      const response = await apiClient.get(`/user-actions/order/${orderName}`)
      return response.data;
    } catch (error) {
      console.error('Error fetching gift card order details:', error);
      throw error;
    }
  }

  // Email Gift Card Codes API
  static async emailGiftCardCodes(orderName) {
    try {
      // SECURE: Middleman endpoint validates ownership; frontend never receives codes
      const response = await apiClient.post(`/user-actions/order/${orderName}/email`, {});
      return response.data;
    } catch (error) {
      console.error('Error emailing gift card codes:', error);
      throw error;
    }
  }

  // Notification APIs
  static async getNotifications(page = 1, limit = 20) {
    try {
      const response = await apiClient.get(`/user-actions/notifications?pagination[page]=${page}&pagination[pageSize]=${limit}&sort=createdAt:desc`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  static async getUnreadNotificationCount() {
    try {
      const response = await apiClient.get('/user-actions/notifications/count');
      return response.data;
    } catch (error) {
      // Don't log this error too loudly as it might just be network noise
      return { count: 0 };
    }
  }

  static async markNotificationRead(id) {
    try {
      const response = await apiClient.put(`/user-actions/notifications/${id}/read`, {});
      return response.data;
    } catch (error) {
      console.error('Error marking notification read:', error);
      throw error;
    }
  }

  static async markAllNotificationsRead() {
    try {
      const response = await apiClient.put('/user-actions/notifications/read-all', {});
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications read:', error);
      throw error;
    }
  }

  // Expose strapiClient for SSE connection
  static strapiClient = apiClient;
}

// Defensive: ensure this helper exists even across odd HMR/module cache states
if (typeof ApiService.emailGiftCardCodes !== 'function') {
  ApiService.emailGiftCardCodes = async (orderName) => {
    const response = await apiClient.post(`/user-actions/order/${orderName}/email`, {});
    return response.data;
  };
}

// Interceptors are handled globally by UniversalApiClient

export default ApiService;
